import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Check, RefreshCw, ChevronLeft, Sparkles, MapPin, Euro, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Branchen mit Dienstleistungs-Vorschlägen
const branchenMitDienstleistungen = {
  handwerk: {
    label: 'Handwerk',
    dienstleistungen: ['Sanitär & Heizung', 'Elektroinstallation', 'Malerei', 'Tischlerei', 'Dachdeckerei', 'Fliesenleger']
  },
  gastronomie: {
    label: 'Gastronomie',
    dienstleistungen: ['Catering', 'Lieferservice', 'Eventlocation', 'Partyservice', 'Mittagsmenü', 'Brunch']
  },
  einzelhandel: {
    label: 'Einzelhandel',
    dienstleistungen: ['Online-Shop', 'Lieferung', 'Click & Collect', 'Beratung', 'Reparaturservice', 'Geschenkservice']
  },
  dienstleistung: {
    label: 'Dienstleistung',
    dienstleistungen: ['Reinigung', 'Umzüge', 'Entrümpelung', 'Gartenarbeit', 'Hausmeisterservice', 'Reparaturen']
  },
  gesundheit: {
    label: 'Gesundheit',
    dienstleistungen: ['Physiotherapie', 'Massage', 'Beratung', 'Hausbesuche', 'Prävention', 'Wellness']
  },
  bildung: {
    label: 'Bildung',
    dienstleistungen: ['Nachhilfe', 'Sprachkurse', 'Workshops', 'Online-Kurse', 'Coaching', 'Prüfungsvorbereitung']
  },
  immobilien: {
    label: 'Immobilien',
    dienstleistungen: ['Vermietung', 'Verkauf', 'Bewertung', 'Hausverwaltung', 'Beratung', 'Finanzierung']
  },
  technologie: {
    label: 'Technologie',
    dienstleistungen: ['IT-Support', 'Webdesign', 'App-Entwicklung', 'SEO', 'Cloud-Services', 'Datensicherheit']
  },
  andere: {
    label: 'Andere',
    dienstleistungen: ['Beratung', 'Service', 'Support', 'Verkauf', 'Schulung', 'Sonstiges']
  }
};

const NewCampaign = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // User Data (vorausgefüllt)
  const [userData, setUserData] = useState(null);
  
  // Step 1 Data
  const [firmenname, setFirmenname] = useState('');
  const [branche, setBranche] = useState('');
  const [standort, setStandort] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Neue Dienstleistung
  const [dienstleistung, setDienstleistung] = useState('');
  const [beschreibung, setBeschreibung] = useState('');
  const [ziele, setZiele] = useState(['Mehr Anrufe']);
  
  // Werbestandort
  const [werbestandortOption, setWerbestandortOption] = useState('stadt');
  const [werbestandorte, setWerbestandorte] = useState('');

  // Step 2 Data
  const [headlines, setHeadlines] = useState(['', '', '', '', '']);
  const [descriptions, setDescriptions] = useState(['', '']);
  const [expertOptimization, setExpertOptimization] = useState(false);

  // Budget
  const [budget, setBudget] = useState(600);
  const [laufzeit, setLaufzeit] = useState('monatlich');
  const [agbAccepted, setAgbAccepted] = useState(false);

  // Fetch user data on mount
  useEffect(() => {
    fetchUserData();
  }, []);

  // Update Werbestandorte based on option
  useEffect(() => {
    if (standort) {
      const city = standort.split(' ').slice(1).join(' ') || standort;
      switch (werbestandortOption) {
        case 'stadt':
          setWerbestandorte(city);
          break;
        case 'umkreis':
          setWerbestandorte(`${city} + 30km Umkreis`);
          break;
        case 'land':
          setWerbestandorte('Österreich');
          break;
        default:
          break;
      }
    }
  }, [werbestandortOption, standort]);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserData(data);
        
        // Vorausfüllen der Felder
        setFirmenname(data.company_name || '');
        setPhoneNumber(data.phone_number || '');
        
        // Versuche Branche aus bestehenden Kampagnen zu laden
        await fetchExistingCampaignData(token);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchExistingCampaignData = async (token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/campaigns/my-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.campaigns && data.campaigns.length > 0) {
          const lastCampaign = data.campaigns[0];
          if (lastCampaign.branche) setBranche(lastCampaign.branche);
          if (lastCampaign.standort) setStandort(lastCampaign.standort);
          if (lastCampaign.website_url) setWebsiteUrl(lastCampaign.website_url);
          if (lastCampaign.phone_number) setPhoneNumber(lastCampaign.phone_number);
          if (lastCampaign.firmenname) setFirmenname(lastCampaign.firmenname);
        }
      }
    } catch (error) {
      console.error('Error fetching campaign data:', error);
    }
  };

  const handleZielToggle = (ziel) => {
    if (ziele.includes(ziel)) {
      setZiele(ziele.filter(z => z !== ziel));
    } else {
      setZiele([...ziele, ziel]);
    }
  };

  const handleDienstleistungSelect = (dl) => {
    setDienstleistung(dl);
  };

  const handleStep1Next = async () => {
    // Debug logging
    console.log('Form values:', { firmenname, branche, standort, dienstleistung, beschreibung, websiteUrl, phoneNumber, ziele });
    
    // Detailed validation with specific error messages
    if (!firmenname) {
      toast.error('Bitte gib einen Firmennamen ein');
      return;
    }
    if (!branche) {
      toast.error('Bitte wähle eine Branche aus');
      return;
    }
    if (!standort) {
      toast.error('Bitte gib einen Standort ein');
      return;
    }
    if (!dienstleistung) {
      toast.error('Bitte gib eine Dienstleistung ein');
      return;
    }
    if (!beschreibung) {
      toast.error('Bitte gib eine Beschreibung ein');
      return;
    }
    if (!websiteUrl) {
      toast.error('Bitte gib eine Website-URL ein');
      return;
    }
    if (!phoneNumber) {
      toast.error('Bitte gib eine Telefonnummer ein');
      return;
    }

    if (ziele.length === 0) {
      toast.error('Bitte wähle mindestens ein Ziel aus');
      return;
    }

    if (werbestandortOption === 'custom' && !werbestandorte.trim()) {
      toast.error('Bitte gib mindestens einen Werbestandort an');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/onboarding/generate-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firmenname,
          branche,
          standort,
          beschreibung: `${dienstleistung}: ${beschreibung}`,
          website_url: websiteUrl,
          phone_number: phoneNumber,
          ziele
        })
      });

      if (!response.ok) throw new Error('Fehler beim Generieren der Anzeigentexte');

      const data = await response.json();
      setHeadlines(data.headlines);
      setDescriptions(data.descriptions);
      setStep(2);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Generieren der Anzeigentexte');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateHeadlines = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/onboarding/generate-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firmenname,
          branche,
          standort,
          beschreibung: `${dienstleistung}: ${beschreibung}`,
          website_url: websiteUrl,
          phone_number: phoneNumber,
          ziele
        })
      });

      if (!response.ok) throw new Error('Fehler beim Regenerieren');

      const data = await response.json();
      setHeadlines(data.headlines);
      toast.success('Neue Anzeigentitel generiert!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Regenerieren der Anzeigentitel');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateDescriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/onboarding/generate-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          firmenname,
          branche,
          standort,
          beschreibung: `${dienstleistung}: ${beschreibung}`,
          website_url: websiteUrl,
          phone_number: phoneNumber,
          ziele
        })
      });

      if (!response.ok) throw new Error('Fehler beim Regenerieren');

      const data = await response.json();
      setDescriptions(data.descriptions);
      toast.success('Neue Anzeigenbeschreibungen generiert!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Fehler beim Regenerieren der Anzeigenbeschreibungen');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    const validHeadlines = headlines.filter(h => h.trim().length > 0);
    const validDescriptions = descriptions.filter(d => d.trim().length > 0);

    if (validHeadlines.length < 3) {
      toast.error('Bitte gib mindestens 3 Headlines ein');
      return;
    }

    if (validDescriptions.length < 1) {
      toast.error('Bitte gib mindestens 1 Beschreibung ein');
      return;
    }

    if (!agbAccepted) {
      toast.error('Bitte stimme den Bedingungen zu, um fortzufahren');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${BACKEND_URL}/api/campaigns/new-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          budget,
          laufzeit,
          expert_optimization: expertOptimization,
          origin_url: window.location.origin,
          campaign_data: {
            firmenname,
            branche,
            standort,
            dienstleistung,
            beschreibung: `${dienstleistung}: ${beschreibung}`,
            website_url: websiteUrl,
            phone_number: phoneNumber,
            ziele,
            werbestandorte,
            headlines: validHeadlines,
            descriptions: validDescriptions,
            budget,
            laufzeit,
            is_additional_campaign: true
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Fehler beim Erstellen der Checkout-Session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Fehler beim Abschluss');
    } finally {
      setLoading(false);
    }
  };

  const managementFee = budget * 0.20;
  const totalAmount = budget + managementFee + (expertOptimization ? 29 : 0);

  // Budget Optionen mit geschätzten Anfragen (40€ pro Anfrage)
  const budgetOptions = [
    { amount: 600, label: 'Starter', anfragen: Math.floor(600 / 40) },
    { amount: 1500, label: 'Optimal', anfragen: Math.floor(1500 / 40) },
    { amount: 3000, label: 'Pro', anfragen: Math.floor(3000 / 40) }
  ];

  // Dienstleistungs-Vorschläge basierend auf Branche
  const dienstleistungsVorschlaege = branche 
    ? branchenMitDienstleistungen[branche]?.dienstleistungen || []
    : [];

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Daten werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      {/* Top Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <img src="/logo.png" alt="AdSimplify Logo" className="h-8 w-8 object-contain" />
              <span className="text-xl font-bold text-gray-900">AdSimplify</span>
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-gray-700 hover:text-gray-900">
              <X className="w-5 h-5 mr-2" />
              Abbrechen
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-4">
            <span className="text-sm font-medium text-gray-600">
              Schritt {step} von 2
            </span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-[#0040FF] h-2 rounded-full transition-all duration-500"
              style={{ width: `${(step / 2) * 100}%` }}
            />
          </div>

          {/* Step Labels */}
          <div className="flex items-center justify-between mt-4">
            <div className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= 1 ? 'bg-[#0040FF] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > 1 ? <Check className="w-5 h-5" /> : '1'}
              </div>
              <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:block">
                Dienstleistung
              </span>
            </div>

            <div className={`flex-1 h-1 mx-2 rounded transition-all ${
              step >= 2 ? 'bg-[#0040FF]' : 'bg-gray-200'
            }`} />

            <div className="flex flex-col items-center flex-1">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= 2 ? 'bg-[#0040FF] text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
              <span className="mt-2 text-xs sm:text-sm font-medium text-gray-700 hidden sm:block">
                Anzeige & Budget
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Dienstleistung definieren */}
        {step === 1 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Form */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Neue Dienstleistung bewerben
                  </h2>
                  <p className="text-gray-600">
                    Welche Dienstleistung möchten Sie zusätzlich bewerben?
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Branche */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Branche *
                    </label>
                    <Select value={branche} onValueChange={setBranche}>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Branche wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(branchenMitDienstleistungen).map(([key, val]) => (
                          <SelectItem key={key} value={key}>
                            {val.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Dienstleistungs-Vorschläge */}
                  {branche && dienstleistungsVorschlaege.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        <Sparkles className="w-4 h-4 inline mr-1 text-[#0040FF]" />
                        Beliebte Dienstleistungen in Ihrer Branche
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {dienstleistungsVorschlaege.map((dl) => (
                          <button
                            key={dl}
                            type="button"
                            onClick={() => handleDienstleistungSelect(dl)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                              dienstleistung === dl
                                ? 'bg-[#0040FF] text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {dl}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dienstleistung Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name der Dienstleistung *
                    </label>
                    <Input
                      type="text"
                      value={dienstleistung}
                      onChange={(e) => setDienstleistung(e.target.value)}
                      placeholder="z.B. Fensterreinigung, Umzugsservice, etc."
                      className="h-11"
                    />
                  </div>

                  {/* Beschreibung */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Beschreibung der Dienstleistung *
                    </label>
                    <Textarea
                      value={beschreibung}
                      onChange={(e) => setBeschreibung(e.target.value)}
                      placeholder="Beschreiben Sie diese Dienstleistung kurz..."
                      rows={3}
                    />
                  </div>

                  {/* Ziele */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Ziel (Mehrfachauswahl) *
                    </label>
                    <div className="space-y-2">
                      {['Mehr Anrufe', 'Mehr Buchungen', 'Mehr Besucher auf Website'].map(ziel => (
                        <div
                          key={ziel}
                          className="flex items-center space-x-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => handleZielToggle(ziel)}
                        >
                          <Checkbox
                            checked={ziele.includes(ziel)}
                            onCheckedChange={() => handleZielToggle(ziel)}
                          />
                          <label className="text-sm font-medium cursor-pointer flex-1">
                            {ziel}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Werbestandort */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Wo soll die Anzeige erscheinen? *
                    </label>
                    
                    {standort && (
                      <p className="text-sm text-gray-500 mb-3">
                        📍 Ihr Standort: <strong>{standort}</strong>
                      </p>
                    )}

                    <div className="space-y-2">
                      <div
                        onClick={() => setWerbestandortOption('stadt')}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          werbestandortOption === 'stadt'
                            ? 'border-[#0040FF] bg-[#E6EDFF]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">Nur in meiner Stadt</div>
                            <div className="text-xs text-gray-600">Empfohlen für lokale Dienstleistungen</div>
                          </div>
                          {werbestandortOption === 'stadt' && <Check className="w-5 h-5 text-[#0040FF]" />}
                        </div>
                      </div>

                      <div
                        onClick={() => setWerbestandortOption('umkreis')}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          werbestandortOption === 'umkreis'
                            ? 'border-[#0040FF] bg-[#E6EDFF]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">Stadt + 30km Umkreis</div>
                            <div className="text-xs text-gray-600">Für regionale Reichweite</div>
                          </div>
                          {werbestandortOption === 'umkreis' && <Check className="w-5 h-5 text-[#0040FF]" />}
                        </div>
                      </div>

                      <div
                        onClick={() => setWerbestandortOption('land')}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          werbestandortOption === 'land'
                            ? 'border-[#0040FF] bg-[#E6EDFF]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium text-gray-900">Ganz Österreich</div>
                            <div className="text-xs text-gray-600">Für überregionale Services</div>
                          </div>
                          {werbestandortOption === 'land' && <Check className="w-5 h-5 text-[#0040FF]" />}
                        </div>
                      </div>

                      <div
                        onClick={() => setWerbestandortOption('custom')}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          werbestandortOption === 'custom'
                            ? 'border-[#0040FF] bg-[#E6EDFF]'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">Eigene Städte eingeben</div>
                            {werbestandortOption === 'custom' && (
                              <Input
                                type="text"
                                value={werbestandorte}
                                onChange={(e) => setWerbestandorte(e.target.value)}
                                placeholder="z.B. Wien, Graz, Salzburg"
                                className="mt-2 h-10"
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                          </div>
                          {werbestandortOption === 'custom' && <Check className="w-5 h-5 text-[#0040FF] ml-2" />}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Firmendaten (vorausgefüllt, editierbar) */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">
                  Ihre Firmendaten
                  <span className="text-xs font-normal text-gray-500 ml-2">(vorausgefüllt)</span>
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Firmenname *</label>
                      <Input
                        type="text"
                        value={firmenname}
                        onChange={(e) => setFirmenname(e.target.value)}
                        placeholder="Ihre Firma"
                        className="h-10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Standort *</label>
                      <Input
                        type="text"
                        value={standort}
                        onChange={(e) => setStandort(e.target.value)}
                        placeholder="z.B. 1010 Wien"
                        className="h-10"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Website *</label>
                      <Input
                        type="url"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://..."
                        className="h-10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefon *</label>
                      <Input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="+43..."
                        className="h-10"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                onClick={handleStep1Next}
                disabled={loading}
                className="w-full h-12 bg-[#0040FF] hover:bg-[#0033CC] text-white font-medium text-base"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generiere Anzeigentexte...
                  </>
                ) : (
                  'Weiter zu Anzeige & Budget →'
                )}
              </Button>
            </div>

            {/* Right: Live Preview */}
            <div className="lg:sticky lg:top-8 lg:self-start">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#0040FF]" />
                    Live-Vorschau
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="default" className="text-xs px-2 py-0.5 bg-gray-900">Anzeige</Badge>
                      <span className="text-xs text-gray-500">•</span>
                      <span className="text-xs text-gray-600">{firmenname || 'Ihre Firma'}</span>
                    </div>
                    
                    <h4 className="text-lg text-[#1a0dab] hover:underline cursor-pointer font-normal mb-1">
                      {dienstleistung 
                        ? `${firmenname || 'Firma'} - ${dienstleistung} - Jetzt anfragen`
                        : 'Ihre Anzeigentitel erscheinen hier'}
                    </h4>
                    
                    <div className="mb-2">
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-gray-700 font-medium">{standort || 'Standort'}</span>
                        <span className="text-gray-400">›</span>
                        <span className="text-gray-700">{branchenMitDienstleistungen[branche]?.label || 'Branche'}</span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      {beschreibung || 'Ihre Anzeigenbeschreibung wird hier angezeigt...'}
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-gray-700">
                      <strong>💡 Tipp:</strong> Je detaillierter Ihre Beschreibung, desto bessere Anzeigentexte generiert unsere KI.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 2: Anzeige & Budget */}
        {step === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Headlines & Descriptions */}
            <div className="space-y-6">
              {/* Headlines Card */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-semibold text-gray-900">
                    Anzeigentitel (max. 30 Zeichen)
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateHeadlines}
                    disabled={loading}
                    className="text-[#0040FF] border-[#0040FF] hover:bg-[#E6EDFF]"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Neu generieren
                  </Button>
                </div>
                <div className="space-y-3">
                  {headlines.map((headline, index) => (
                    <div key={index}>
                      <Input
                        type="text"
                        value={headline}
                        onChange={(e) => {
                          const newHeadlines = [...headlines];
                          newHeadlines[index] = e.target.value.slice(0, 30);
                          setHeadlines(newHeadlines);
                        }}
                        placeholder={`Titel ${index + 1}`}
                        maxLength={30}
                        className="h-10"
                      />
                      <div className="flex justify-between mt-1 px-1">
                        <span className="text-xs text-gray-500">#{index + 1}</span>
                        <span className={`text-xs ${headline.length > 25 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                          {headline.length}/30
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Descriptions Card */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-semibold text-gray-900">
                    Anzeigenbeschreibung (max. 90 Zeichen)
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateDescriptions}
                    disabled={loading}
                    className="text-[#0040FF] border-[#0040FF] hover:bg-[#E6EDFF]"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Neu generieren
                  </Button>
                </div>
                <div className="space-y-3">
                  {descriptions.map((desc, index) => (
                    <div key={index}>
                      <Textarea
                        value={desc}
                        onChange={(e) => {
                          const newDescriptions = [...descriptions];
                          newDescriptions[index] = e.target.value.slice(0, 90);
                          setDescriptions(newDescriptions);
                        }}
                        placeholder={`Beschreibung ${index + 1}`}
                        maxLength={90}
                        rows={2}
                      />
                      <div className="flex justify-between mt-1 px-1">
                        <span className="text-xs text-gray-500">#{index + 1}</span>
                        <span className={`text-xs ${desc.length > 80 ? 'text-red-600 font-medium' : 'text-gray-400'}`}>
                          {desc.length}/90
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Budget Selection */}
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="mb-4">
                  <label className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <Euro className="w-4 h-4" />
                    Budget-Empfehlung für &quot;{dienstleistung}&quot;
                  </label>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  {budgetOptions.map((option) => (
                    <div
                      key={option.amount}
                      onClick={() => setBudget(option.amount)}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all text-center ${
                        budget === option.amount
                          ? 'border-[#0040FF] bg-[#E6EDFF]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-xl font-bold text-gray-900">{option.amount}€</div>
                      <div className="text-sm font-medium text-gray-700">{option.label}</div>
                      <div className="text-xs text-[#0040FF] mt-1">~{option.anfragen} Anfragen</div>
                      {budget === option.amount && (
                        <Check className="w-5 h-5 text-[#0040FF] mx-auto mt-2" />
                      )}
                    </div>
                  ))}
                </div>

                {/* Custom Budget Input */}
                <div className="mb-4">
                  <label className="block text-sm text-gray-600 mb-2">Oder eigenes Budget eingeben:</label>
                  <div className="relative">
                    <Input
                      type="number"
                      value={budget}
                      onChange={(e) => setBudget(Math.max(100, parseInt(e.target.value) || 0))}
                      className="h-12 text-lg font-bold pr-8"
                      min={100}
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-lg font-bold text-gray-400">€</span>
                  </div>
                </div>

                {/* Disclaimer */}
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      <strong>Hinweis:</strong> Die geschätzten Anfragen basieren auf unseren Erfahrungswerten (~40€/Anfrage) und sind nicht garantiert. Wir optimieren Ihre Kampagne kontinuierlich, um die Kosten pro Anfrage unter den Branchendurchschnitt zu bringen.
                    </p>
                  </div>
                </div>

                {/* Laufzeit */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">Laufzeit</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      onClick={() => setLaufzeit('monatlich')}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                        laufzeit === 'monatlich'
                          ? 'border-[#0040FF] bg-[#E6EDFF]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">Monatlich</div>
                      <div className="text-xs text-gray-600">Auto-Verlängerung</div>
                    </div>
                    <div
                      onClick={() => setLaufzeit('einmalig')}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                        laufzeit === 'einmalig'
                          ? 'border-[#0040FF] bg-[#E6EDFF]'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">Einmalig</div>
                      <div className="text-xs text-gray-600">Keine Verlängerung</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Experten-Optimierung Upsell */}
              <Card 
                className={`cursor-pointer transition-all ${
                  expertOptimization 
                    ? 'border-[#0040FF] border-2 shadow-lg' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setExpertOptimization(!expertOptimization)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">Experten-Optimierung</h4>
                      <p className="text-sm text-gray-600">Professionell optimierte Texte für maximale Klickrate</p>
                    </div>
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-3 ${
                      expertOptimization ? 'bg-[#0040FF] border-[#0040FF]' : 'border-gray-300'
                    }`}>
                      {expertOptimization && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm font-medium text-gray-700">Einmalig</span>
                    <span className="text-lg font-bold text-[#0040FF]">29 €</span>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Summary */}
              <div className="bg-gray-50 rounded-lg p-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Werbebudget:</span>
                  <span className="font-medium">{budget.toFixed(2)} €</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Management-Fee (20%):</span>
                  <span className="font-medium">{managementFee.toFixed(2)} €</span>
                </div>
                {expertOptimization && (
                  <div className="flex justify-between text-sm pt-3 border-t">
                    <span className="text-gray-700 font-medium">Experten-Optimierung:</span>
                    <span className="font-medium text-[#0040FF]">29.00 €</span>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between">
                  <span className="font-bold text-gray-900">Gesamtbetrag:</span>
                  <span className="font-bold text-[#0040FF] text-xl">{totalAmount.toFixed(2)} €</span>
                </div>
                <div className="text-xs text-gray-600 text-center">
                  {laufzeit === 'monatlich' ? '* Monatlich abgerechnet' : '* Einmalige Zahlung'}
                </div>
              </div>

              {/* AGB Checkbox */}
              <div className="flex items-start space-x-3 p-4 border rounded-lg bg-white">
                <Checkbox
                  id="agb"
                  checked={agbAccepted}
                  onCheckedChange={setAgbAccepted}
                />
                <label htmlFor="agb" className="text-sm text-gray-700 cursor-pointer flex-1">
                  Ich stimme zu, dass AdSimplify meine Anzeige verwaltet und die Kosten gemäß der gewählten Laufzeit abgerechnet werden. *
                </label>
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-11">
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button
                  onClick={handleComplete}
                  disabled={loading}
                  className="flex-1 h-11 bg-[#0040FF] hover:bg-[#0033CC] text-white font-medium"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Verarbeite...
                    </>
                  ) : (
                    `Kampagne starten für ${totalAmount.toFixed(2)} €`
                  )}
                </Button>
              </div>
            </div>

            {/* Right: Live Preview */}
            <div className="lg:sticky lg:top-8 lg:self-start space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Vorschau: Ihre Anzeige</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4 bg-white">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="default" className="text-xs px-2 py-0.5 bg-gray-900">Anzeige</Badge>
                      <span className="text-xs text-gray-500">•</span>
                      <span className="text-xs text-gray-600">{firmenname || 'example.com'}</span>
                    </div>
                    
                    <h4 className="text-lg text-[#1a0dab] hover:underline cursor-pointer font-normal mb-1">
                      {headlines.filter(h => h.trim()).slice(0, 3).join(' - ') || 'Ihre Anzeigentitel'}
                    </h4>
                    
                    <div className="mb-2">
                      <div className="flex items-center gap-1 text-sm">
                        <span className="text-gray-700 font-medium">{standort || 'Wien'}</span>
                        <span className="text-gray-400">›</span>
                        <span className="text-gray-700">{dienstleistung || 'Dienstleistung'}</span>
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      {descriptions[0] || 'Ihre Anzeigenbeschreibung'}
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-gray-700">
                      <strong>💡 Tipp:</strong> Google zeigt unterschiedliche Kombinationen Ihrer Texte an.
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Zusammenfassung */}
              <Card className="bg-gradient-to-br from-[#0040FF] to-[#0030CC] text-white border-0">
                <CardContent className="p-6">
                  <h4 className="font-semibold mb-3">Ihre neue Kampagne</h4>
                  <div className="space-y-2 text-sm text-white/90">
                    <div className="flex justify-between">
                      <span>Dienstleistung:</span>
                      <span className="font-medium text-white">{dienstleistung || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Werbegebiet:</span>
                      <span className="font-medium text-white">{werbestandorte || '-'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Budget:</span>
                      <span className="font-medium text-white">{budget}€ / {laufzeit === 'monatlich' ? 'Monat' : 'einmalig'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewCampaign;
